// AN-7: what the pager does when a GIF fails to load with the app open.
//
// The CDN is opened by a signed COOKIE, not by a signature in the URL
// (infra/animations.ts): the URL never changes, so the disk cache survives a
// rotation. That is also why the cure has two steps. The host renews the
// cookie (`recover`), then the <Image> is REMOUNTED on the same URL through
// its `key`, and the new request carries the new cookie. Nothing keeps the
// failure: CloudFront's 403 has no Cache-Control or validators (seen with
// curl, 25/09), Fresco and r-n-w's ImageLoader only remember successes. So a
// remount goes to the network again. ⛔ A cache-bust param would work too,
// and would store the recovered GIF under a URL nobody asks for next time.
//
// ⛔ The bound: ONE recovery per URL until that URL loads. `recover` is a
// network call, and a retry that fails again must stop on today's empty box,
// not loop. A success re-arms the URL, so a rotation much later in the same
// mount can still recover once.
import { useCallback, useEffect, useRef, useState } from 'react';

export type AnimationImageRecovery = {
  key: string;
  onError: () => void;
  onLoad: () => void;
};

export function useAnimationLoadRecovery(
  recover: (() => Promise<void>) | undefined,
): (url: string) => AnimationImageRecovery {
  const [attempts, setAttempts] = useState<Record<string, number>>({});
  // URLs that already spent their recovery and have not loaded since.
  const spent = useRef(new Set<string>());
  const recoverRef = useRef(recover);
  recoverRef.current = recover;
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // ⛔ Stable per URL: r-n-w's <Image> reloads whenever its `onError` or
  // `onLoad` changes identity, so a fresh arrow each render would re-request.
  const handlers = useRef(new Map<string, Omit<AnimationImageRecovery, 'key'>>());
  const handlersFor = useCallback((url: string) => {
    let h = handlers.current.get(url);
    if (!h) {
      h = {
        onLoad: () => {
          spent.current.delete(url);
        },
        onError: () => {
          const run = recoverRef.current;
          if (!run || spent.current.has(url)) return;
          spent.current.add(url);
          // The executor runs `run` NOW, and turns a sync throw into a reject.
          new Promise<void>((resolve) => resolve(run())).then(
            () => {
              if (!alive.current) return;
              setAttempts((a) => ({ ...a, [url]: (a[url] ?? 0) + 1 }));
            },
            () => {}, // no access, no retry: the empty box stays
          );
        },
      };
      handlers.current.set(url, h);
    }
    return h;
  }, []);

  return (url) => ({ key: `${url}#${attempts[url] ?? 0}`, ...handlersFor(url) });
}
